import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, Clock, Upload, FileText, Globe2, Loader2, LogOut, Shield, AlertCircle, AlarmClock, CreditCard, ExternalLink, FileSignature } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { portal, portalDocs, invoices as invoicesApi, esign as esignApi } from '@/lib/api';
import SignaturePad from '@/components/SignaturePad';
import { safeHtml } from '@/lib/sanitize';

export default function PortalPage() {
  const nav = useNavigate();
  const [data, setData] = React.useState(null);
  const [docs, setDocs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [tab, setTab] = React.useState(() => {
    // Reminder-email deep link may open ?tab=invoices — respect it on first paint
    if (typeof window === 'undefined') return 'timeline';
    const p = new URLSearchParams(window.location.search);
    const t = p.get('tab');
    if (t === 'invoices' || t === 'esign') return t;
    return 'timeline';
  });
  const [esignItems, setEsignItems] = React.useState([]);
  const [esignFocus, setEsignFocus] = React.useState(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('sign');
  });

  const loadEsign = React.useCallback(() => {
    esignApi.portal.list().then((r) => setEsignItems(r.items || [])).catch(() => setEsignItems([]));
  }, []);

  const loadDocs = () => portalDocs.list().then(setDocs).catch(() => setDocs([]));

  React.useEffect(() => {
    if (!portal.isAuthed()) { nav('/login'); return; }
    Promise.all([portal.matter(), portalDocs.list().catch(() => [])])
      .then(([d, docList]) => { setData(d); setDocs(docList || []); })
      .catch((e) => setErr(e?.response?.data?.detail || 'Session invalid — please sign in again.'))
      .finally(() => setLoading(false));
    loadEsign();
  }, [nav, loadEsign]);

  const signOut = () => { portal.logout(); nav('/login'); };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#F4F7FA]"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>;
  if (err || !data) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#F4F7FA] p-6">
      <div className="font-serif text-2xl text-[#1A3A5C]">Please sign in</div>
      <div className="text-sm text-slate-500">{err || 'Your portal session has expired.'}</div>
      <Link to="/login"><Button className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">Go to login</Button></Link>
    </div>
  );

  const m = data.matter;
  const events = data.events || [];

  const timeline = [
    { step: 1, label: 'Consumer Guide & Fee Agreement signed', at: m.consumer_guide_signed ? '✓' : 'pending', done: !!m.consumer_guide_signed },
    { step: 2, label: 'Skills assessment', at: m.assessing_body ? `via ${m.assessing_body}` : 'n/a', done: !!m.assessing_body },
    { step: 3, label: 'Documents uploaded & verified', at: `${docs.filter(d => d.status === 'verified').length}/${docs.length} verified`, done: docs.length > 0 && docs.every(d => d.status === 'verified') },
    { step: 4, label: m.deadline_label || 'Next action', at: m.next_deadline || '—', done: false, current: true },
    { step: 5, label: 'Lodgement with DHA', at: 'Target', done: ['LODGED','DECIDED'].includes(m.stage) },
    { step: 6, label: 'Case officer decision', at: '—', done: m.stage === 'DECIDED' },
  ];

  return (
    <div className="min-h-screen bg-[#F4F7FA] text-[#0A0A0A]">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <img src="/immipro-logo.png" alt="ImmiPro" className="h-8 w-auto" />
            <div>
              <div className="font-serif text-lg font-bold text-[#1A3A5C]">ImmiPro</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Client Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50">
              <Globe2 className="h-3.5 w-3.5" /> English (AU)
            </button>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2ECC9A] text-xs font-bold text-[#1A3A5C]">
              {m.applicant.split(' ').map(s=>s[0]).join('').slice(0,2).toUpperCase()}
            </div>
            <button onClick={signOut} className="text-slate-400 hover:text-[#1A3A5C]" title="Sign out"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Hello, {m.applicant.split(' ')[0]}.</h1>
          <div className="mt-1 text-sm text-slate-600">Subclass {m.subclass}{m.stream ? ` · ${m.stream}` : ''} · Managed by {m.assigned_to_name}</div>
          <div className="mt-1 text-xs text-slate-400">Signed in as {data.email} · Matter {m.ref}</div>
        </div>

        {m.next_deadline && (() => {
          const dueMs = new Date(m.next_deadline).getTime();
          const days = Math.ceil((dueMs - Date.now()) / (1000 * 60 * 60 * 24));
          const label = (m.deadline_label || '').toLowerCase();
          const isCritical = ['medical', 'police', 'health', 'afp'].some((k) => label.includes(k));
          const urgent = Number.isFinite(days) && days <= 7 && days >= 0;
          const overdue = Number.isFinite(days) && days < 0;

          if (overdue) {
            return (
              <Card data-testid="portal-deadline-overdue" className="mt-6 border-red-400 bg-gradient-to-br from-red-50 to-white shadow-sm">
                <CardContent className="flex items-center justify-between gap-4 p-5">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
                      <AlarmClock className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-widest text-red-700">Overdue by {Math.abs(days)} day{Math.abs(days) === 1 ? '' : 's'}</div>
                      <div className="mt-1 font-serif text-xl font-bold text-red-900">{m.deadline_label || 'Action required'}</div>
                      <div className="mt-0.5 text-xs text-red-800/80">Please contact your agent immediately — this step blocks your lodgement.</div>
                    </div>
                  </div>
                  <div className="text-right text-xs text-red-700">
                    <div className="font-semibold">Due</div>
                    <div>{m.next_deadline}</div>
                  </div>
                </CardContent>
              </Card>
            );
          }

          if (urgent) {
            return (
              <Card
                data-testid="portal-deadline-nudge"
                data-critical={isCritical ? 'true' : 'false'}
                className="mt-6 border-orange-400 bg-gradient-to-br from-orange-50 to-white shadow-sm animate-pulse-slow"
              >
                <CardContent className="flex items-center justify-between gap-4 p-5">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100">
                      <AlarmClock className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-widest text-orange-700">
                        {days === 0 ? 'Due today' : `Due in ${days} day${days === 1 ? '' : 's'}`}
                        {isCritical ? ' · Critical' : ''}
                      </div>
                      <div className="mt-1 font-serif text-xl font-bold text-orange-900">{m.deadline_label || 'Action required'}</div>
                      <div className="mt-0.5 text-xs text-orange-800/80">
                        {isCritical
                          ? 'Health & character checks often have 4–8 week processing times — booking today keeps your lodgement on track.'
                          : 'Please complete this step before the due date to avoid delays.'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right text-xs text-orange-700">
                    <div className="font-semibold">Due</div>
                    <div>{m.next_deadline}</div>
                  </div>
                </CardContent>
              </Card>
            );
          }

          // Default: green "next step" card (existing behaviour)
          return (
            <Card className="mt-6 border-[#2ECC9A] bg-gradient-to-br from-[#2ECC9A]/10 to-white shadow-sm">
              <CardContent className="flex items-center justify-between gap-6 p-6">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-[#2ECC9A]">What we need from you next</div>
                  <div className="mt-2 font-serif text-2xl font-bold text-[#1A3A5C]">{m.deadline_label || 'Action required'}</div>
                  <div className="mt-2 text-sm text-slate-600">Due by <span className="font-medium text-[#1A3A5C]">{m.next_deadline}</span>{Number.isFinite(days) ? ` · ${days} day${days === 1 ? '' : 's'} to go` : ''}</div>
                </div>
                <div className="hidden md:flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-[#2ECC9A]/20">
                  <Clock className="h-10 w-10 text-[#2ECC9A]" />
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {(() => {
          const pending = docs.filter((d) => d.status !== 'verified');
          if (pending.length === 0) return null;
          return (
            <Card
              data-testid="portal-upload-nudge"
              className="mt-6 border-amber-300 bg-gradient-to-br from-amber-50 to-white shadow-sm"
            >
              <CardContent className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-amber-900">
                      {pending.length} document{pending.length > 1 ? 's' : ''} waiting on your declaration
                    </div>
                    <div className="mt-0.5 text-xs text-amber-800/80">
                      Your agent can&rsquo;t lodge with DHA until each upload is e-signed under the Statutory Declarations Act 1959 (Cth). It takes about a minute.
                    </div>
                  </div>
                </div>
                <Button
                  data-testid="portal-upload-nudge-cta"
                  onClick={() => setTab('docs')}
                  className="h-9 shrink-0 bg-amber-600 text-white hover:bg-amber-700"
                >
                  Review &amp; sign
                </Button>
              </CardContent>
            </Card>
          );
        })()}

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList>
            <TabsTrigger value="timeline" data-testid="portal-tab-timeline">Timeline</TabsTrigger>
            <TabsTrigger value="docs" data-testid="portal-tab-docs">Documents ({docs.length})</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="portal-tab-invoices">Invoices</TabsTrigger>
            <TabsTrigger value="esign" data-testid="portal-tab-esign">
              Sign documents{esignItems.some((e) => e.status === 'pending') && (
                <Badge className="ml-1.5 h-4 min-w-[16px] rounded-full bg-red-500 px-1 text-[9px] text-white">
                  {esignItems.filter((e) => e.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="portal-tab-activity">Recent activity</TabsTrigger>
            <TabsTrigger value="pathway" data-testid="portal-tab-pathway">My migration options</TabsTrigger>
            <TabsTrigger value="questionnaire" data-testid="portal-tab-questionnaire">Intake questionnaire</TabsTrigger>
          </TabsList>

          <TabsContent value="timeline" className="mt-6">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="text-sm font-semibold text-[#1A3A5C]">Your application journey</div>
                <div className="relative mt-6 space-y-6 border-l-2 border-slate-200 pl-6">
                  {timeline.map((t) => (
                    <div key={t.step} className="relative">
                      <div className={`absolute -left-[33px] flex h-6 w-6 items-center justify-center rounded-full ${
                        t.done ? 'bg-[#2ECC9A] text-white' : t.current ? 'bg-white ring-4 ring-[#2ECC9A]/30 text-[#2ECC9A]' : 'bg-white ring-2 ring-slate-300 text-slate-400'}`}>
                        {t.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="text-[10px] font-bold">{t.step}</span>}
                      </div>
                      <div className="pl-4">
                        <div className={`text-sm font-medium ${t.current ? 'text-[#1A3A5C]' : t.done ? 'text-slate-500' : 'text-slate-500'}`}>{t.label}</div>
                        <div className="text-xs text-slate-500">{t.at}</div>
                        {t.current && <Badge className="mt-2 bg-[#2ECC9A]/10 text-[#2ECC9A] border-[#2ECC9A]/30">Action required</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="docs" className="mt-6">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-[#1A3A5C]">Your documents</div>
                    <div className="text-xs text-slate-500">Uploads are encrypted at rest. Signing the declaration is required before your agent can lodge.</div>
                  </div>
                  <ClientUploadDialog onUploaded={loadDocs} />
                </div>
                <div className="mt-4 space-y-2">
                  {docs.length === 0 && <div className="rounded-md border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">No documents yet. Click Upload to add your first file.</div>}
                  {docs.map((d) => (
                    <div key={d.id} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 p-3">
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-slate-500" />
                        <div>
                          <div className="text-sm font-medium text-[#1A3A5C]">{d.file_name}</div>
                          <div className="text-xs text-slate-500">{d.doc_type} · {(d.size_bytes / 1024).toFixed(1)} KB</div>
                        </div>
                      </div>
                      {d.status === 'verified' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                          <CheckCircle2 className="h-3 w-3" /> Declaration signed
                        </span>
                      ) : (
                        <ClientDeclarationDialog doc={d} onSigned={loadDocs} />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="invoices" className="mt-6">
            <PortalInvoicesPanel />
          </TabsContent>

          <TabsContent value="esign" className="mt-6">
            <PortalEsignPanel
              items={esignItems}
              onRefresh={loadEsign}
              initialFocus={esignFocus}
              onFocusHandled={() => setEsignFocus(null)}
            />
          </TabsContent>

          <TabsContent value="activity" className="mt-6">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="text-sm font-semibold text-[#1A3A5C]">Recent activity on your matter</div>
                <div className="mt-4 space-y-3">
                  {events.length === 0 && <div className="text-sm text-slate-500">No activity yet.</div>}
                  {events.map((e) => (
                    <div key={e.id} className="flex gap-3 rounded-md border border-slate-100 bg-slate-50 p-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#2ECC9A]/10"><FileText className="h-4 w-4 text-[#2ECC9A]" /></div>
                      <div className="flex-1">
                        <div className="text-xs text-slate-500">{e.actor} · {new Date(e.at).toLocaleString()}</div>
                        <div className="mt-1 text-sm text-slate-700">{e.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pathway" className="mt-6">
            <PortalPathwayPanel />
          </TabsContent>
          <TabsContent value="questionnaire" className="mt-6">
            <div className="rounded-lg border border-slate-200 bg-white p-6 text-center">
              <div className="text-sm font-semibold text-[#1A3A5C]">Complete your intake questionnaire</div>
              <p className="mt-1 text-xs text-slate-500">
                Your migration adviser needs a few structured answers to prepare your application.
                You can save progress at any time.
              </p>
              <a href="/portal/questionnaire" data-testid="portal-open-questionnaire"
                 className="mt-3 inline-block rounded-md bg-[#2ECC9A] px-4 py-2 text-xs font-semibold text-[#0F1F3A] hover:bg-[#2ECC9A]/90">
                Open questionnaire
              </a>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <SignatureVaultTile />

      <footer className="border-t border-slate-200 bg-white py-6">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 text-xs text-slate-500">
          <div>Secured by ImmiPro · AU-hosted · ISO 27001:2022 aligned</div>
          <div>Language: English (AU) → 12 languages coming soon</div>
        </div>
      </footer>
    </div>
  );
}

function ClientUploadDialog({ onUploaded }) {
  const [open, setOpen] = React.useState(false);
  const [file, setFile] = React.useState(null);
  const [docType, setDocType] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');

  const submit = async (e) => {
    e.preventDefault(); if (!file) return;
    setErr(''); setSaving(true);
    try { await portalDocs.upload(file, docType); setOpen(false); setFile(null); setDocType(''); onUploaded?.(); }
    catch (er) { setErr(er?.response?.data?.detail || 'Upload failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-9 gap-1.5 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90"><Upload className="h-4 w-4" /> Upload document</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Upload a document</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Document type (optional)</Label>
            <Input value={docType} onChange={(e)=>setDocType(e.target.value)} placeholder="Passport, Police check, Medical…" className="mt-1" />
          </div>
          <div><Label>File</Label>
            <Input type="file" required onChange={(e)=>setFile(e.target.files?.[0] || null)} className="mt-1" />
            <div className="mt-1 text-xs text-slate-500">Max 15 MB per file. AES-256 encrypted at rest.</div>
          </div>
          {err && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{err}</div>}
          <DialogFooter>
            <Button type="submit" disabled={saving || !file} className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} Upload
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ClientDeclarationDialog({ doc, onSigned }) {
  const [open, setOpen] = React.useState(false);
  const [signed, setSigned] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const submit = async () => {
    setSaving(true);
    try { await portalDocs.signDeclaration(doc.id); setOpen(false); setSigned(false); onSigned?.(); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-8 gap-1 border-[#2ECC9A]/30 text-[#2ECC9A] hover:bg-[#2ECC9A]/5"><Shield className="h-3.5 w-3.5" /> Sign declaration</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Statutory declaration</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm text-slate-700">
          <div className="rounded-md bg-[#F4F7FA] p-4">
            I solemnly and sincerely declare that <strong>{doc.file_name}</strong> is a true, correct and complete copy of my document and is not fraudulent or altered in any way. I make this declaration under the Statutory Declarations Act 1959 (Cth). I understand a false declaration may attract criminal penalties.
          </div>
          <div className="flex items-start gap-2">
            <Checkbox id="selfack" checked={signed} onCheckedChange={(v)=>setSigned(!!v)} />
            <label htmlFor="selfack" className="text-xs text-slate-600 cursor-pointer">I have read and understood the declaration above. My tick constitutes my e-signature.</label>
          </div>
        </div>
        <DialogFooter>
          <Button disabled={!signed || saving} onClick={submit} className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} E-sign &amp; timestamp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PortalInvoicesPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState('');
  const [err, setErr] = React.useState('');
  const [autoPayId, setAutoPayId] = React.useState(null);
  const [receiptId, setReceiptId] = React.useState(null);

  // Read deep-link ?pay=<invoice_id> from the URL ONCE on mount. After the
  // invoice list loads and matches, we auto-launch Stripe checkout so the
  // client's journey from email → paid is a single tap.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams(window.location.search);
    const pay = p.get('pay');
    if (pay) {
      setAutoPayId(pay);
      // Scrub the query string so a refresh doesn't re-launch checkout.
      const url = new URL(window.location.href);
      url.searchParams.delete('pay');
      url.searchParams.delete('tab');
      window.history.replaceState({}, '', url.pathname + (url.search || ''));
    }
  }, []);

  const load = React.useCallback(() => {
    setLoading(true);
    invoicesApi.portalList().then((r) => setItems(r.items || [])).catch((e) => setErr(e?.response?.data?.detail || 'Failed to load invoices')).finally(() => setLoading(false));
  }, []);
  React.useEffect(load, [load]);

  // Auto-poll every 20s while there's any invoice in-flight (initiated / unpaid → could flip to paid/refunded).
  React.useEffect(() => {
    const hasInFlight = items.some((i) => i.status === 'unpaid' || i.status === 'initiated');
    if (!hasInFlight) return;
    const iv = setInterval(() => {
      invoicesApi.portalList().then((r) => setItems(r.items || [])).catch(() => {});
    }, 20000);
    return () => clearInterval(iv);
  }, [items]);

  const pay = React.useCallback(async (id) => {
    setBusy(id); setErr('');
    try {
      const { checkout_url } = await invoicesApi.checkout(id, window.location.origin);
      window.location.href = checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not start checkout');
      setBusy('');
    }
  }, []);

  // Auto-launch checkout after the invoice list arrives — but only if we know
  // the invoice still exists AND is not already paid.
  React.useEffect(() => {
    if (!autoPayId || loading) return;
    const target = items.find((i) => i.id === autoPayId);
    if (!target) { setAutoPayId(null); return; }
    if (target.status === 'paid') { setAutoPayId(null); return; }
    setAutoPayId(null);
    pay(autoPayId);
  }, [autoPayId, loading, items, pay]);

  const total = items.filter((i) => i.status !== 'paid').reduce((s, i) => s + Number(i.amount || 0), 0);

  return (
    <Card className="border-slate-200" data-testid="portal-invoices-panel">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-[#1A3A5C]">Invoices &amp; payments</div>
            <div className="mt-1 text-xs text-slate-500">Pay migration disbursements securely with Stripe (Visa · Mastercard · Amex · PayID).</div>
          </div>
          {total > 0 && (
            <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Outstanding: <span className="font-mono font-semibold">A${total.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="mt-6 flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#2ECC9A]" /></div>
        ) : items.length === 0 ? (
          <div className="mt-6 rounded-md border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500">
            No invoices right now — your agent will send disbursement requests here.
          </div>
        ) : (
          <div className="mt-4 space-y-3" data-testid="portal-invoices-list">
            {items.map((inv) => {
              const paid = inv.status === 'paid';
              const refunded = inv.status === 'refunded';
              return (
                <div key={inv.id} data-testid={`portal-invoice-${inv.id}`} className={`rounded-md border p-4 ${paid ? 'border-emerald-200 bg-emerald-50/40' : refunded ? 'border-slate-300 bg-slate-50' : 'border-slate-200'}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-[#1A3A5C]">{inv.description}</div>
                        {refunded ? (
                          <Badge variant="outline" className="border-slate-300 bg-white text-[10px] text-slate-600" data-testid={`portal-invoice-refunded-${inv.id}`}>
                            <AlertCircle className="mr-1 h-3 w-3" /> Refunded
                          </Badge>
                        ) : paid ? (
                          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700">
                            <CheckCircle2 className="mr-1 h-3 w-3" /> Paid
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-[10px] text-amber-700">Unpaid</Badge>
                        )}
                      </div>
                      {inv.line_items && inv.line_items.length > 1 && (
                        <ul className="mt-2 space-y-0.5 text-[11px] text-slate-500">
                          {inv.line_items.map((li, i) => (
                            <li key={i} className="flex justify-between">
                              <span>{li.description}</span>
                              <span className="font-mono">A${Number(li.amount || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {refunded && (
                        <div className="mt-2 rounded-md border border-slate-200 bg-white p-2 text-[11px] text-slate-600">
                          <div className="font-medium text-[#1A3A5C]">Refund processed</div>
                          <div className="mt-0.5">A${Number(inv.refund_amount || inv.amount).toLocaleString('en-AU', { minimumFractionDigits: 2 })} returned to your original payment method{inv.refunded_at ? ` on ${new Date(inv.refunded_at).toLocaleDateString('en-AU')}` : ''}. Funds usually clear within 5–10 business days.</div>
                          {inv.refund_reason && <div className="mt-1 italic text-slate-500">Reason: {inv.refund_reason}</div>}
                          <div className="mt-1 text-[10px] text-slate-400">This does not affect your migration matter. Your agent has already reviewed.</div>
                        </div>
                      )}
                      {inv.due_date && !refunded && <div className="mt-1 text-[11px] text-slate-500">Due {inv.due_date}</div>}
                    </div>
                    <div className="text-right">
                      <div className={`font-mono text-lg font-bold ${refunded ? 'text-slate-500 line-through' : 'text-[#1A3A5C]'}`}>A${Number(inv.amount).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</div>
                      {!paid && !refunded && (
                        <Button
                          data-testid={`portal-pay-${inv.id}`}
                          onClick={() => pay(inv.id)}
                          disabled={busy === inv.id}
                          className="mt-2 h-8 gap-1 bg-[#1A3A5C] px-3 text-xs text-white hover:bg-[#1A3A5C]/90"
                        >
                          {busy === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard className="h-3 w-3" />}
                          Pay with Stripe
                        </Button>
                      )}
                      {paid && (
                        <Button
                          data-testid={`portal-view-receipt-${inv.id}`}
                          onClick={() => setReceiptId(inv.id)}
                          variant="outline"
                          className="mt-2 h-8 gap-1 border-emerald-200 bg-white px-3 text-xs text-emerald-700 hover:bg-emerald-50"
                        >
                          <FileText className="h-3 w-3" /> View receipt
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {err && <div className="mt-3 rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="portal-invoices-error">{err}</div>}

        <div className="mt-4 flex items-center gap-2 text-[10px] text-slate-400">
          <Shield className="h-3 w-3" /> Payments are processed by Stripe. ImmiPro never sees your card details.
        </div>
      </CardContent>
      <PortalReceiptDialog invoiceId={receiptId} onClose={() => setReceiptId(null)} />
      <PortalPaymentHistory onView={(id) => setReceiptId(id)} />
    </Card>
  );
}


function PortalPaymentHistory({ onView }) {
  const [items, setItems] = React.useState(null);
  const [totalPaid, setTotalPaid] = React.useState(0);
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    invoicesApi.portalHistory().then((r) => { setItems(r.items || []); setTotalPaid(r.total_paid_aud || 0); setCount(r.total || 0); }).catch(() => setItems([]));
  }, []);
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-6" data-testid="portal-payment-history">
      <div className="flex items-center justify-between text-xs">
        <div className="font-semibold text-[#1A3A5C]">Payment history</div>
        <div className="text-slate-500">
          {count} receipt{count === 1 ? '' : 's'} · A${totalPaid.toLocaleString()} lifetime paid
        </div>
      </div>
      <div className="mt-2 overflow-hidden rounded-md border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Invoice</th>
              <th className="px-3 py-2 font-medium">Description</th>
              <th className="px-3 py-2 font-medium text-right">Amount</th>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-t border-slate-100" data-testid={`portal-history-${i.id}`}>
                <td className="px-3 py-2 font-mono text-[10px]">{i.invoice_number}</td>
                <td className="px-3 py-2 text-slate-700">{i.description || '—'}</td>
                <td className="px-3 py-2 text-right font-semibold">A${i.amount_aud.toLocaleString()}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500">
                  {i.paid_at ? new Date(i.paid_at).toLocaleDateString('en-AU') : '—'}
                  {i.status === 'refunded' && <span className="ml-1 text-red-500">(refunded)</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {i.status === 'paid' && (
                    <button onClick={() => onView(i.id)} data-testid={`portal-history-view-${i.id}`}
                            className="rounded border border-emerald-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-50">
                      Receipt
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function PortalReceiptDialog({ invoiceId, onClose }) {
  const [src, setSrc] = React.useState('');
  const [err, setErr] = React.useState('');
  React.useEffect(() => {
    if (!invoiceId) { setSrc(''); return; }
    setErr(''); setSrc('');
    const token = localStorage.getItem('immipro_portal_token');
    (async () => {
      try {
        const { API_BASE } = await import('@/lib/api');
        const r = await fetch(`${API_BASE}/portal/invoices/${invoiceId}/receipt.html`, {
          headers: { Authorization: `Bearer ${token || ''}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setSrc(await r.text());
      } catch (e) {
        setErr('Could not load receipt. Please refresh and try again.');
      }
    })();
  }, [invoiceId]);
  const downloadPdf = async () => {
    const token = localStorage.getItem('immipro_portal_token');
    const { API_BASE } = await import('@/lib/api');
    const r = await fetch(`${API_BASE}/portal/invoices/${invoiceId}/receipt.pdf`, {
      headers: { Authorization: `Bearer ${token || ''}` },
    });
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `receipt-${invoiceId.slice(0, 8)}.pdf`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
  };
  if (!invoiceId) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="portal-receipt-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4 text-emerald-600" /> Payment receipt</DialogTitle></DialogHeader>
        {err ? (
          <div className="rounded-md bg-red-50 p-3 text-xs text-red-700">{err}</div>
        ) : !src ? (
          <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <iframe title="receipt" sandbox="" srcDoc={src}
                  className="h-[520px] w-full rounded-md border border-slate-200"
                  data-testid="portal-receipt-iframe" />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button data-testid="portal-receipt-download" onClick={downloadPdf} disabled={!src}
                  className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
            <FileText className="mr-1 h-4 w-4" /> Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// Client-facing pathway panel — read-only "your options" view with translate + Q&A.
function PortalPathwayPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [lang, setLang] = React.useState(() => localStorage.getItem('immipro_portal_lang') || 'en');
  const [translations, setTranslations] = React.useState(null);
  const [trBusy, setTrBusy] = React.useState(false);
  const [questions, setQuestions] = React.useState([]);
  const [askOpen, setAskOpen] = React.useState(false);
  const [askText, setAskText] = React.useState('');
  const [askVisa, setAskVisa] = React.useState('');
  const [askBusy, setAskBusy] = React.useState(false);

  React.useEffect(() => {
    portal.pathway()
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || 'Unable to load'))
      .finally(() => setLoading(false));
    portal.listQuestions().then((r) => setQuestions(r.items || [])).catch(() => {});
  }, []);

  // Collect all translatable strings (labels + reasons) so we can bulk-translate.
  const stringsToTranslate = React.useMemo(() => {
    if (!data) return [];
    const out = [];
    (data.recommended || []).forEach((r) => {
      out.push(r.label || '');
      out.push(r.reason || '');
    });
    if (data.narrative) out.push(data.narrative);
    return out.filter(Boolean);
  }, [data]);

  React.useEffect(() => {
    localStorage.setItem('immipro_portal_lang', lang);
    if (lang === 'en' || !stringsToTranslate.length) { setTranslations(null); return; }
    setTrBusy(true);
    portal.translate(lang, stringsToTranslate)
      .then((r) => setTranslations(r.translations))
      .catch(() => setTranslations(null))
      .finally(() => setTrBusy(false));
  }, [lang, stringsToTranslate]);

  const tr = (idx) => (translations && translations[idx]) || stringsToTranslate[idx] || '';

  const submitQuestion = async () => {
    if (!askText.trim()) return;
    setAskBusy(true);
    try {
      const r = await portal.submitQuestion({ text: askText, visa: askVisa || null });
      setQuestions((qs) => [r.question, ...qs]);
      setAskText(''); setAskVisa(''); setAskOpen(false);
    } finally { setAskBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>;
  if (err) return <div className="rounded-md border border-red-200 bg-red-50 p-4 text-xs text-red-700">{err}</div>;
  if (!data) return null;

  const rec = data.recommended || [];
  const tone = { yes: 'border-emerald-300 bg-emerald-50/50', likely: 'border-amber-300 bg-amber-50/50' };
  // Build translation index — labels are at even positions, reasons at odd, narrative at end
  const narrativeIdx = rec.length * 2;
  const labelIdx = (i) => i * 2;
  const reasonIdx = (i) => i * 2 + 1;

  return (
    <Card className="border-slate-200" data-testid="portal-pathway-panel">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <FileText className="h-4 w-4 text-[#2ECC9A]" /> Your migration options
            </div>
            <div className="text-xs text-slate-500">Prepared by your Registered Migration Agent — informational only.</div>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="portal-language"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700"
              title="Translate this page"
            >
              <option value="en">English</option>
              <option value="hi">हिन्दी (Hindi)</option>
              <option value="zh-CN">中文 (Chinese)</option>
              <option value="vi">Tiếng Việt</option>
              <option value="ne">नेपाली (Nepali)</option>
              <option value="pa">ਪੰਜਾਬੀ (Punjabi)</option>
              <option value="ar">العربية</option>
              <option value="es">Español</option>
              <option value="fil">Filipino</option>
              <option value="ur">اردو</option>
            </select>
            {trBusy && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" data-testid="portal-translate-busy" />}
          </div>
        </div>

        {!data.analysed_at ? (
          <div className="mt-6 rounded-md border border-dashed border-slate-200 p-8 text-center text-xs text-slate-500" data-testid="portal-pathway-empty">
            {data.note || 'Your migration adviser has not yet published a pathway analysis for you.'}
          </div>
        ) : (
          <>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <div className="rounded-md bg-slate-50 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Estimated points</div>
                <div className="text-xl font-bold text-[#1A3A5C]" data-testid="portal-pathway-points">{data.points_estimate?.total ?? '—'}</div>
              </div>
              <div className="text-[10px] text-slate-500">Last updated: {new Date(data.analysed_at).toLocaleString('en-AU')}</div>
              <Button
                data-testid="portal-ask-btn"
                onClick={() => setAskOpen((o) => !o)}
                variant="outline"
                size="sm"
                className="ml-auto h-8 gap-1 border-[#2ECC9A]/40 text-xs text-[#1A3A5C] hover:bg-[#2ECC9A]/10"
              >
                <FileText className="h-3 w-3" /> Ask a question
              </Button>
            </div>

            {askOpen && (
              <div className="mt-4 rounded-md border border-[#2ECC9A]/30 bg-[#2ECC9A]/5 p-4" data-testid="portal-ask-panel">
                <div className="text-xs font-semibold text-[#1A3A5C]">Send your migration agent a question</div>
                <div className="mt-1 text-[11px] text-slate-500">Your agent will reply here — you'll also get a notification.</div>
                <textarea
                  data-testid="portal-ask-text"
                  value={askText}
                  onChange={(e) => setAskText(e.target.value)}
                  rows={3}
                  className="mt-2 w-full rounded-md border border-slate-200 bg-white p-2 text-xs"
                  placeholder="e.g. Which English test should I take for the 189?"
                />
                <div className="mt-2 flex items-center gap-2">
                  <input
                    data-testid="portal-ask-visa"
                    value={askVisa}
                    onChange={(e) => setAskVisa(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                    placeholder="Anchor to visa (optional, e.g. 189)"
                    className="h-8 flex-1 rounded-md border border-slate-200 bg-white px-2 text-xs"
                  />
                  <Button data-testid="portal-ask-submit" onClick={submitQuestion} disabled={askBusy || !askText.trim()} size="sm" className="h-8 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
                    {askBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Send'}
                  </Button>
                </div>
              </div>
            )}

            {data.narrative && (
              <div className="mt-4 rounded-md border border-[#2ECC9A]/30 bg-[#2ECC9A]/5 p-4 text-xs text-slate-700" data-testid="portal-pathway-narrative">
                {(translations ? translations[narrativeIdx] : data.narrative).split('\n\n').map((p, i) => <p key={i} className={i > 0 ? 'mt-2' : ''}>{p}</p>)}
              </div>
            )}

            <div className="mt-6 text-xs font-semibold text-[#1A3A5C]">Recommended pathways ({rec.length})</div>
            <div className="mt-2 grid gap-3 md:grid-cols-2" data-testid="portal-pathway-list">
              {rec.map((r, i) => (
                <div key={r.visa + i} data-testid={`portal-pathway-item-${i}`} className={`rounded-md border p-3 ${tone[r.eligible] || 'border-slate-200 bg-slate-50/40'}`}>
                  <div className="text-sm font-semibold text-[#1A3A5C]">Visa {r.visa} · {translations ? translations[labelIdx(i)] : r.label}</div>
                  <div className="mt-1 text-xs text-slate-700">{translations ? translations[reasonIdx(i)] : r.reason}</div>
                  {r.gaps?.length > 0 && (
                    <div className="mt-2 text-[11px]">
                      <div className="uppercase tracking-wider text-slate-500 text-[9px]">Discuss with your agent</div>
                      <ul className="mt-1 list-disc pl-4 text-slate-600">
                        {r.gaps.slice(0, 2).map((g, j) => <li key={j}>{g}</li>)}
                      </ul>
                    </div>
                  )}
                  <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#1A3A5C] hover:text-[#2ECC9A]">
                    Learn more (DHA) →
                  </a>
                </div>
              ))}
            </div>

            {questions.length > 0 && (
              <div className="mt-6" data-testid="portal-questions-thread">
                <div className="text-xs font-semibold text-[#1A3A5C]">Your questions ({questions.length})</div>
                <div className="mt-2 space-y-3">
                  {questions.map((q) => (
                    <div key={q.id} data-testid={`portal-q-${q.id}`} className="rounded-md border border-slate-200 bg-white p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-xs text-[#1A3A5C]"><b>You asked{q.visa ? ` about visa ${q.visa}` : ''}:</b> {q.text}</div>
                        <Badge variant="outline" className={`shrink-0 text-[10px] ${
                          q.status === 'answered' || q.status === 'resolved'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-amber-200 bg-amber-50 text-amber-700'
                        }`}>{q.status}</Badge>
                      </div>
                      {(q.replies || []).map((r) => (
                        <div key={r.id} className="mt-2 rounded bg-slate-50 p-2 text-xs text-slate-700">
                          <div className="text-[10px] text-slate-500">Reply from {r.author_name || r.author_email} · {new Date(r.created_at).toLocaleString('en-AU')}</div>
                          <div className="mt-1 whitespace-pre-line">{r.text}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}



function PortalEsignPanel({ items, onRefresh, initialFocus, onFocusHandled }) {
  const [selectedId, setSelectedId] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [signature, setSignature] = React.useState('');
  const [typedName, setTypedName] = React.useState('');
  const [agree, setAgree] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [signed, setSigned] = React.useState(null);
  const [savedSignature, setSavedSignature] = React.useState(null); // {typed_name, signature_data_url, updated_at}

  // Fetch the client's saved signature template once (may 404 if never signed before)
  React.useEffect(() => {
    esignApi.portal.savedSignature()
      .then((r) => setSavedSignature(r))
      .catch(() => setSavedSignature(null));
  }, []);

  React.useEffect(() => {
    if (initialFocus && items.some((i) => i.id === initialFocus)) {
      setSelectedId(initialFocus);
      onFocusHandled?.();
    } else if (!selectedId && items.length > 0) {
      setSelectedId(items[0].id);
    }
  }, [initialFocus, items]);

  React.useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setLoading(true); setError(''); setSigned(null);
    setTypedName(''); setSignature(''); setAgree(false);
    esignApi.portal.get(selectedId)
      .then((r) => {
        setDetail(r);
        if (r.status === 'signed') setSigned(r);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load document'))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const submitSign = async () => {
    if (!detail || !signature || !typedName.trim() || !agree) return;
    setSubmitting(true); setError('');
    try {
      const r = await esignApi.portal.sign(detail.id, {
        typed_name: typedName.trim(),
        signature_data_url: signature,
        agree: true,
      });
      setSigned(r);
      onRefresh?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Signing failed');
    } finally { setSubmitting(false); }
  };

  const downloadSigned = async () => {
    try {
      const blob = await esignApi.portal.certificatePdf(detail.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signed-${detail.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Certificate download failed'); }
  };

  if (items.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-8 text-center text-sm text-slate-500" data-testid="portal-esign-empty">
          <FileSignature className="mx-auto mb-2 h-6 w-6 text-slate-300" />
          <div>No documents awaiting your signature right now.</div>
          <div className="mt-1 text-xs text-slate-400">Your agent will email you a link when a document is ready to sign.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]" data-testid="portal-esign-panel">
      <Card className="border-slate-200">
        <CardContent className="p-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Documents ({items.length})
          </div>
          <div className="mt-2 space-y-1">
            {items.map((i) => (
              <button
                key={i.id}
                data-testid={`portal-esign-item-${i.id}`}
                onClick={() => setSelectedId(i.id)}
                className={`w-full rounded-md border p-2 text-left transition ${
                  selectedId === i.id
                    ? 'border-[#2ECC9A] bg-[#2ECC9A]/5'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 text-xs font-semibold text-[#1A3A5C]">{i.title}</div>
                  <Badge variant="outline" className={`shrink-0 text-[9px] capitalize ${
                    i.status === 'pending'
                      ? 'border-amber-200 bg-amber-50 text-amber-700'
                      : i.status === 'signed'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 bg-slate-50 text-slate-500'
                  }`}>{i.status}</Badge>
                </div>
                <div className="mt-0.5 text-[10px] text-slate-500">
                  {new Date(i.created_at).toLocaleDateString('en-AU')}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-6">
          {loading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#2ECC9A]" /></div>}
          {!loading && detail && (
            <>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="text-lg font-semibold text-[#1A3A5C]" data-testid="portal-esign-title">{detail.title}</div>
                  <div className="text-xs text-slate-500">
                    From your migration adviser · matter {detail.matter_ref}
                  </div>
                </div>
                <Badge variant="outline" className={`text-[10px] capitalize ${
                  detail.status === 'signed'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : detail.status === 'cancelled'
                      ? 'border-slate-200 bg-slate-50 text-slate-500'
                      : 'border-amber-200 bg-amber-50 text-amber-700'
                }`}>{detail.status}</Badge>
              </div>

              {detail.source_type === 'pdf' && detail.source_pdf_gridfs_id ? (
                <div className="mt-4" data-testid="portal-esign-source-pdf">
                  <iframe
                    src={esignApi.portal.sourcePdfUrl(detail.id)}
                    className="h-[500px] w-full rounded-md border border-slate-200"
                    title="Document to sign"
                  />
                  <div className="mt-1 text-[10px] text-slate-500">
                    Uploaded PDF · {detail.source_pdf_filename}
                  </div>
                </div>
              ) : (
                <div className="mt-4 max-h-96 overflow-auto rounded-md border border-slate-200 bg-white p-6 shadow-inner"
                     data-testid="portal-esign-body"
                     dangerouslySetInnerHTML={{ __html: safeHtml(detail.body_html) }} />
              )}
              {detail.status === 'pending' && !signed && (
                <div className="mt-6 rounded-md border border-[#2ECC9A]/30 bg-[#2ECC9A]/5 p-4" data-testid="portal-esign-signblock">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
                    <FileSignature className="h-4 w-4 text-[#2ECC9A]" /> Sign to accept
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-slate-500">Type your full legal name</label>
                      <Input
                        data-testid="portal-esign-name"
                        value={typedName}
                        onChange={(e) => setTypedName(e.target.value)}
                        placeholder="e.g. Jane Applicant"
                        className="mt-1 h-9 text-sm font-serif"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] uppercase tracking-wider text-slate-500">Draw your signature</label>
                        {savedSignature && !signature && (
                          <button
                            type="button"
                            data-testid="portal-esign-use-previous"
                            onClick={() => {
                              setSignature(savedSignature.signature_data_url);
                              if (!typedName.trim() && savedSignature.typed_name) {
                                setTypedName(savedSignature.typed_name);
                              }
                            }}
                            className="text-[10px] font-medium text-[#2ECC9A] hover:text-[#0F766E] underline underline-offset-2"
                          >
                            Use my previous signature
                          </button>
                        )}
                      </div>
                      <div className="mt-1">
                        {savedSignature && signature === savedSignature.signature_data_url ? (
                          <div data-testid="portal-esign-signature-reused"
                               className="rounded-md border border-emerald-300 bg-white p-3 text-center">
                            <img src={signature} alt="Your saved signature" className="mx-auto max-h-20" />
                            <div className="mt-1 flex items-center justify-center gap-2 text-[10px] text-emerald-700">
                              <CheckCircle2 className="h-3 w-3" /> Reusing your saved signature
                              <button type="button" onClick={() => setSignature('')}
                                      className="ml-1 text-slate-400 underline">Draw a new one</button>
                            </div>
                          </div>
                        ) : (
                          <SignaturePad value={signature} onChange={setSignature} />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex items-start gap-2 rounded-md bg-white p-3">
                    <Checkbox
                      data-testid="portal-esign-agree"
                      id="esign-agree"
                      checked={agree}
                      onCheckedChange={(v) => setAgree(!!v)}
                      className="mt-0.5 data-[state=checked]:bg-[#2ECC9A] data-[state=checked]:border-[#2ECC9A]"
                    />
                    <label htmlFor="esign-agree" className="text-xs leading-snug text-slate-600">
                      I agree that my typed name and drawn signature constitute my legally binding electronic signature on this document. I understand my IP address and browser fingerprint will be recorded for audit purposes.
                    </label>
                  </div>

                  {error && <div className="mt-2 rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="portal-esign-error">{error}</div>}

                  <div className="mt-3 flex justify-end">
                    <Button
                      data-testid="portal-esign-submit"
                      onClick={submitSign}
                      disabled={submitting || !signature || !typedName.trim() || !agree}
                      className="gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSignature className="h-4 w-4" /> Sign document</>}
                    </Button>
                  </div>
                </div>
              )}

              {(signed || detail.status === 'signed') && (
                <div className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 p-4" data-testid="portal-esign-signed">
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                    <CheckCircle2 className="h-4 w-4" /> Signed
                  </div>
                  <div className="mt-1 text-xs text-emerald-700">
                    Signed by <b>{(signed || detail).signed_name}</b> on {new Date((signed || detail).signed_at).toLocaleString('en-AU')}. A copy has been saved to your matter&apos;s document vault.
                  </div>
                  {(signed || detail).signature_data_url && (
                    <div className="mt-3 rounded-md bg-white p-3 text-center">
                      <img src={(signed || detail).signature_data_url} alt="Your signature" className="mx-auto max-h-20" />
                      <div className="mt-1 font-serif text-base text-[#1A3A5C]">{(signed || detail).signed_name}</div>
                    </div>
                  )}
                  <Button
                    data-testid="portal-esign-download"
                    onClick={downloadSigned}
                    size="sm"
                    variant="outline"
                    className="mt-3 gap-1 border-emerald-300 bg-white text-emerald-800 text-xs"
                  >
                    <FileText className="h-3 w-3" /> Download signed PDF
                  </Button>
                </div>
              )}

              {detail.status === 'cancelled' && (
                <div className="mt-6 rounded-md bg-slate-50 p-4 text-sm text-slate-600" data-testid="portal-esign-cancelled">
                  This request was cancelled by your agent.
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function SignatureVaultTile() {
  const [saved, setSaved] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [clearing, setClearing] = React.useState(false);
  const load = React.useCallback(() => {
    setLoading(true);
    esignApi.portal.savedSignature()
      .then(setSaved)
      .catch(() => setSaved(null))
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(load, [load]);
  const clear = async () => {
    if (!window.confirm('Delete your saved signature? You can always draw a new one when signing your next document.')) return;
    setClearing(true);
    try { await esignApi.portal.deleteSavedSignature(); setSaved(null); }
    catch { /* noop */ }
    finally { setClearing(false); }
  };
  if (loading || !saved) return null;
  return (
    <section className="mx-auto mt-6 max-w-5xl px-6" data-testid="portal-signature-vault">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#2ECC9A]/10">
            <FileSignature className="h-4 w-4 text-[#2ECC9A]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-[#1A3A5C]">Your saved signature</div>
            <div className="text-[11px] text-slate-500">
              Saved {new Date(saved.updated_at).toLocaleDateString('en-AU')} · reused automatically on the next document you sign
            </div>
            {saved.signature_data_url && (
              <div className="mt-2 inline-block rounded-md border border-dashed border-[#2ECC9A]/40 bg-[#2ECC9A]/5 px-4 py-2">
                <img
                  src={saved.signature_data_url}
                  alt="Your saved signature"
                  data-testid="portal-signature-vault-preview"
                  className="max-h-16"
                />
                <div className="mt-0.5 font-serif text-xs text-[#1A3A5C]">{saved.typed_name}</div>
              </div>
            )}
          </div>
          <button
            data-testid="portal-signature-vault-clear"
            onClick={clear}
            disabled={clearing}
            className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-[11px] font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
          >
            {clearing ? 'Clearing…' : 'Clear signature'}
          </button>
        </div>
      </div>
    </section>
  );
}
